/**
 * DefenseBuilderField Component
 *
 * Static field view for the Custom Defense Builder. Shows the 7 defensive positions
 * (aligned per whatever coverage is currently assigned to each) plus a reference
 * offensive alignment (WR1-4, RB) so a coach can see who each defender is near.
 *
 * No animation/playback here — this is a builder, not the simulator. Clicking a defender
 * selects him so the sidebar's PositionAssignmentSelector can change his assignment.
 */

import { DefensePositionId, CustomDefenseAssignments, FormationType, PlayerPositionId } from '../types';
import {
  getCB1StartPosition,
  getDBLB1StartPosition,
  getFSStartPosition,
  getCB2StartPosition,
  getDBLB2StartPosition,
  getSSStartPosition,
  getLBStartPosition,
  getAssignmentOption,
  DEFENSE_POSITION_LABELS,
} from '../rules';
import {
  DEFAULT_WR_POSITION,
  DEFAULT_WR2_POSITION,
  DEFAULT_WR3_POSITION,
  DEFAULT_WR4_POSITION,
  DEFAULT_RB_POSITION,
} from '../context/SimulationContext';
import Player from './Player';
import FieldMarkings from './FieldMarkings';
import './DefenseBuilderField.css';

interface DefenseBuilderFieldProps {
  assignments: CustomDefenseAssignments;
  selectedPosition: DefensePositionId;
  onSelectPosition: (position: DefensePositionId) => void;
  formation: FormationType;
}

/** Short badge text for a defender showing his current assignment. */
/**
 * Short on-field badge for a defender's assignment.
 *
 * Covers every coverage the assignment registry can produce. It previously mapped only `man` and
 * `cover4Match`, and the fallback was `?? coverage` — so a defender assigned Cover 3 Match wore the
 * literal string `cover3Match` on the field, leaking an internal camelCase id to a coach.
 *
 * `assertAllCoveragesBadged` below keeps this honest as coverages are added.
 */
const COVERAGE_BADGES: Record<string, string> = {
  man: 'MAN',
  cover3Match: 'C3M',
  cover4Match: 'C4',
  cover4Palms: 'PALMS',
  cover6: 'C6',
};

/**
 * Last resort if a coverage is registered without a badge.
 *
 * Produces something readable from the id (`cover3Match` -> `COVER 3 MATCH`) rather than showing the
 * id verbatim. Adding the coverage to COVERAGE_BADGES above is still the right fix — this only makes
 * the failure mode presentable.
 */
function badgeFor(coverage: string): string {
  return (
    COVERAGE_BADGES[coverage] ??
    coverage
      .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
      .toUpperCase()
  );
}

/** Offense reference labels — match the simulator's per-player names (WR1-4, RB). */
const OFFENSE_LABELS: Partial<Record<PlayerPositionId, string>> = {
  wr1: 'WR1',
  wr2: 'WR2',
  wr3: 'WR3',
  wr4: 'WR4',
  rb: 'RB',
};

function DefenseBuilderField({ assignments, selectedPosition, onSelectPosition, formation }: DefenseBuilderFieldProps) {
  // Each defender aligns per whatever coverage he's currently assigned — reuses the exact
  // same start-position getters the live simulator uses, so the builder preview matches reality.
  const cb1Position = getCB1StartPosition(assignments.cb1, formation);
  const dblb1Position = getDBLB1StartPosition(assignments.dblb1, formation);
  const fsPosition = getFSStartPosition(assignments.fs, formation);
  const ssPosition = getSSStartPosition(assignments.ss, formation);
  const dblb2Position = getDBLB2StartPosition(assignments.dblb2, formation);
  const cb2Position = getCB2StartPosition(assignments.cb2, formation);
  const lbPosition = getLBStartPosition(assignments.lb, formation);

  const defenders: { id: DefensePositionId; position: ReturnType<typeof getCB1StartPosition> }[] = [
    { id: 'cb1', position: cb1Position },
    { id: 'dblb1', position: dblb1Position },
    { id: 'fs', position: fsPosition },
    { id: 'ss', position: ssPosition },
    { id: 'dblb2', position: dblb2Position },
    { id: 'cb2', position: cb2Position },
    { id: 'lb', position: lbPosition },
  ];

  const offense: { id: PlayerPositionId; position: ReturnType<typeof getCB1StartPosition> }[] = [
    { id: 'wr1', position: DEFAULT_WR_POSITION },
    { id: 'wr2', position: DEFAULT_WR2_POSITION },
    { id: 'wr3', position: DEFAULT_WR3_POSITION },
    { id: 'wr4', position: DEFAULT_WR4_POSITION },
    { id: 'rb', position: DEFAULT_RB_POSITION },
  ];

  return (
    <div className="defense-builder-field">
      {/* Same turf and yardage as the simulator, so a defense looks the same in both places.
          First in the DOM so it sits behind the players. */}
      <FieldMarkings />
      {offense.map(({ id, position }) => (
        <Player
          key={id}
          position={position}
          playerType="WR"
          isAnimating={false}
          positionId={id}
          label={OFFENSE_LABELS[id] ?? id.toUpperCase()}
        />
      ))}

      {defenders.map(({ id, position }) => {
        const coverage = assignments[id];
        const option = getAssignmentOption(id, coverage);
        return (
          <Player
            key={id}
            position={position}
            playerType="CB"
            isAnimating={false}
            positionId={id}
            label={DEFENSE_POSITION_LABELS[id]}
            onClick={() => onSelectPosition(id)}
            isSelected={selectedPosition === id}
            badge={badgeFor(coverage)}
            hoverDescription={{
              title: option?.label ?? coverage,
              description: option?.isImplemented
                ? option.description ?? ''
                : `${option?.description ?? 'Not built yet.'} Click to change.`,
            }}
          />
        );
      })}

    </div>
  );
}

export default DefenseBuilderField;
